import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useUser } from "../contexts/useUser";
import Service from "./Service";
import { useLocation, useNavigate } from "react-router-dom";
import { useGoogleAnalytics } from "../hooks/useGoogleAnalytics";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMGroups } from "../dataaccess/fetchLLMGroups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useLLM } from "llmasaservice-client";

const Services: React.FC = () => {
  const { project } = useUser();
  const navigate = useNavigate();
  const { logEvent } = useGoogleAnalytics();
  const theme = useTheme();
  const [lastGroupTestClicked, setLastGroupTestClicked] = React.useState("");
  const [compactView, setCompactView] = useState(() => {
    const savedCompactView = localStorage.getItem("compactView");
    return savedCompactView === "true";
  });

  const location = useLocation();
  const { newService } = location.state?.data || {};

  const [hideInactive, setHideInactive] = useState(() => {
    const savedHideInactive = localStorage.getItem("hideInactive");
    return (
      savedHideInactive === "true" &&
      (newService === undefined || newService === false)
    );
  });

  useEffect(() => {
    console.log("newService", newService);
    if (newService) {
      setHideInactive(false);
    }
  }, [newService]);

  useEffect(() => {
    localStorage.setItem("compactView", compactView.toString());
  }, [compactView]);

  useEffect(() => {
    localStorage.setItem("hideInactive", hideInactive.toString());
  }, [hideInactive]);

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const {
    send,
    response: streamData,
    idle,
  } = useLLM({
    project_id: project?.id ?? "",
    url: url,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["groups"],
    queryFn: () => fetchLLMGroups(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error...</div>;
  }

  const groups = data?.data?.groupsByProjectId?.items ?? [];

  return (
    <>
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{ borderRadius: 3, border: "1px solid #444" }}
            >
              <Box p={2}>
                <Typography variant="caption">
                  Create connections to your chosen LLM vendors and models
                  (services). Group services together to allow calls to be made
                  to services in that group manually or to allow routing rules
                  to target specific groups.
                </Typography>
                <br />
                <Box display="flex" gap={2} marginTop={2}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      logEvent("Services", "Add Service Clicked");
                      navigate("/modelpricing");
                    }}
                  >
                    Add Service
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      logEvent("Services", "Add Service Group Clicked");
                      navigate("/groups/new");
                    }}
                  >
                    Add Service Group
                  </Button>
                  <FormControlLabel
                    control={<Checkbox />}
                    checked={compactView}
                    onChange={() => setCompactView((v) => !v)}
                    label="Compact View"
                  />
                  <FormControlLabel
                    control={<Checkbox />}
                    checked={hideInactive}
                    onChange={() => setHideInactive((v) => !v)}
                    label="Hide Inactive Services"
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  padding: "0 16px", // Adjust padding as needed
                  minHeight: "20px", // Set the minimum height
                  '&.Mui-expanded': {
                    minHeight: "20px", // Ensure the height remains the same when expanded
                  },
                  '.MuiAccordionSummary-content': {
                    margin: "8px 0", // Adjust the margin to control the content spacing
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Tooltip title="The default group of services called when group isn't specified. Typically this is the moderate to lower cost models.">
                    <Typography>Group: Default</Typography>
                  </Tooltip>
                  <Box>
                    {streamData && lastGroupTestClicked === "" && (
                      <>
                        <br />
                        <Typography variant="caption">
                          &nbsp;&nbsp;Response: {streamData}{" "}
                          {streamData.includes("2") ? (
                            <CheckCircleIcon style={{ color: "green" }} />
                          ) : (
                            <CancelIcon style={{ color: "red" }} />
                          )}
                        </Typography>
                      </>
                    )}
                    <Button
                      sx={{ marginRight: 1 }}
                      onClick={(event) => {
                        event.stopPropagation();
                        send("1+1=", [], [], true, false);

                        setLastGroupTestClicked("");
                      }}
                    >
                      Test
                    </Button>
                    <Button
                      onClick={(event) => {
                        event.stopPropagation();
                        navigator.clipboard.writeText(
                          `// import the hook
import { useLLM } from "llmasaservice-client";

// in your component, destructure the send/response/idle functions from the hook
const { send, response, idle } = useLLM({
  project_id: "${project?.id ?? ""}", // your unique project id

  // optional - identify your customer
  customer: { 
    customer_id: "123", 
    customer_name: "Our Best Customer, Inc.", 
  }
});

// call the send function (within an async function block) WITH THE SERVICE GROUP ID as shown 
// (parameters: prompt, messages, stream response? true/false, caching allowed? true/false, serviceGroupId, abortController, onCompleteCallback, onErrorCallback)
await send("1+1=", [], [], false);
console.log(response);`
                        );
                      }}
                    >
                      Copy Code
                    </Button>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: theme.palette.background.default,
                  borderRadius: "0 0 16px 16px",
                  border: "1px solid #444",
                  borderTop: "none",
                }}
              >
                <Box
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="flex-start"
                  alignItems="center"
                  gap={2}
                  m={1}
                >
                  <Box
                    p={2}
                    gap={2}
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    {project?.services?.items
                      ?.filter(
                        (service) =>
                          (!service?.groupId || service?.groupId === "") &&
                          (!hideInactive || service?.status === "active")
                      )
                      .map((service) => (
                        <Service
                          key={service?.id}
                          service={service}
                          compact={compactView}
                        />
                      ))}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {groups
              .sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""))
              .map((group) => (
                <Accordion key={group?.id} defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      padding: "0 16px", // Adjust padding as needed
                      minHeight: "20px", // Set the minimum height
                      '&.Mui-expanded': {
                        minHeight: "20px", // Ensure the height remains the same when expanded
                      },
                      '.MuiAccordionSummary-content': {
                        margin: "8px 0", // Adjust the margin to control the content spacing
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Tooltip title={group?.description}>
                        <Typography>Group: {group?.name}</Typography>
                      </Tooltip>
                      <Box>
                        {streamData &&
                          lastGroupTestClicked === (group?.id ?? "") && (
                            <>
                              <br />
                              <Typography variant="caption">
                                &nbsp;&nbsp;Response: {streamData}{" "}
                                {streamData.includes("2") ? (
                                  <CheckCircleIcon style={{ color: "green" }} />
                                ) : (
                                  <CancelIcon style={{ color: "red" }} />
                                )}
                              </Typography>
                            </>
                          )}
                        <Button
                          sx={{ marginRight: 1 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            send("1+1=", [], [], true, false, group?.id ?? "");
                            setLastGroupTestClicked(group?.id ?? "");
                          }}
                        >
                          Test
                        </Button>
                        <Button
                          sx={{ marginRight: 1 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            navigator.clipboard.writeText(
                              `// import the hook
import { useLLM } from "llmasaservice-client";

// in your component, destructure the send/response/idle functions from the hook
const { send, response, idle } = useLLM({
  project_id: "${project?.id ?? ""}", // your unique project id

  // optional - identify your customer
  customer: { 
    customer_id: "123", 
    customer_name: "Our Best Customer, Inc.", 
  }
});

// call the send function (within an async function block) WITH THE SERVICE GROUP ID as shown 
// (parameters: prompt, messages, stream response? true/false, caching allowed? true/false, serviceGroupId, abortController, onCompleteCallback, onErrorCallback)
await send("1+1=", [], [], false, true, "${group?.id ?? ""}");
console.log(response);`
                            );
                          }}
                        >
                          Copy Code
                        </Button>
                        <Button
                          onClick={() => navigate(`/groups/edit/${group?.id}`)}
                        >
                          Edit
                        </Button>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      backgroundColor: theme.palette.background.default,
                      borderRadius: "0 0 16px 16px",
                      border: "1px solid #444",
                      borderTop: "none",
                    }}
                  >
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      justifyContent="flex-start"
                      alignItems="center"
                      gap={2}
                      m={1}
                      sx={{
                        backgroundColor: theme.palette.background.default,
                      }}
                    >
                      <Box
                        p={2}
                        gap={2}
                        display="flex"
                        flexWrap="wrap"
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        {project?.services?.items
                          ?.filter(
                            (service) =>
                              service?.groupId === group?.id &&
                              (!hideInactive || service?.status === "active")
                          )
                          .map((service) => (
                            <Service
                              key={service?.id}
                              service={service}
                              compact={compactView}
                            />
                          ))}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Services;
